import Button from '../components/Button'
import CtaBand from '../components/CtaBand'
import Icon from '../components/Icon'
import PageHero from '../components/PageHero'
import Process from '../components/sections/Process'
import Reveal from '../components/Reveal'
import { Section, SectionHead } from '../components/Section'
import { ServiceArt } from '../components/art/LineArt'
import { serviceListJsonLd, useJsonLd, useSeo } from '../lib/seo'
import { hy } from '../lib/typography'

/**
 * The three mandates.
 *
 * `text` is verbatim from the old site; `points` restate the same sentence as
 * scannable items. `id` is the anchor the jump cards link to, `art` selects the
 * illustration in components/art/LineArt.jsx.
 */
const SERVICES = [
  {
    id: 'weg-verwaltung',
    label: 'Für Eigentümergemeinschaften',
    title: 'WEG-Verwaltung',
    text: 'Unsere WEG-Verwaltung bietet Ihnen eine umfassende Betreuung Ihrer Wohnungseigentümergemeinschaft. Wir organisieren Eigentümerversammlungen, kümmern uns um die Finanzverwaltung und stehen Ihnen bei allen Belangen rund um das gemeinschaftliche Eigentum zur Seite.',
    points: [
      'Vorbereitung, Durchführung und Protokollierung der Eigentümerversammlung',
      'Finanzverwaltung, Wirtschaftsplan und Jahresabrechnung',
      'Umsetzung der gefassten Beschlüsse',
      'Ansprechpartner für alle Belange des gemeinschaftlichen Eigentums',
    ],
    art: 'building',
  },
  {
    id: 'sondereigentum',
    label: 'Für einzelne Eigentumswohnungen',
    // 25 characters: needs an explicit break point at phone widths.
    title: hy('Sonder', 'eigentums', 'verwaltung'),
    text: 'Für eine individuelle Betreuung Ihrer Eigentumswohnung stehen wir Ihnen mit unserer Sondereigentumsverwaltung zur Verfügung. Wir setzen auf eine umfassende Pflege Ihrer Immobilien, damit diese langfristig ihren Wert behalten und optimal genutzt werden können.',
    points: [
      'Individuelle Betreuung Ihrer einzelnen Einheit',
      'Umfassende Pflege für den langfristigen Werterhalt',
      'Optimale Nutzung der Wohnung im Blick',
      'Abstimmung mit der WEG-Verwaltung des Objekts',
    ],
    art: 'apartment',
  },
  {
    id: 'mietverwaltung',
    label: 'Für Mehrfamilienhäuser',
    title: 'Mietverwaltung',
    text: 'Verwandeln Sie Ihr Mehrfamilienhaus vom potenziellen Problemfall in einen zuverlässigen Renditebringer. Das Casa-Team sichert Ihnen mit modernen Werkzeugen und sensibler Handhabung eine sorgenfreie Mietverwaltung zu. Vertrauen Sie Ihr Objekt den Profis an - bei uns sind Sie in sicheren Händen.',
    points: [
      'Neuvermietung im fliegenden Wechsel, um Leerstand zu vermeiden',
      'Reparatur- und Hausmeistermanagement',
      'Betriebskostenverwaltung und transparente Jahresabrechnung',
      'Kommunikation mit den Mietern und regelmäßige Begehungen',
    ],
    art: 'keys',
  },
]

/** The three management types as Service entities. Stable reference for
    `useJsonLd`, which compares by identity. */
const servicesJsonLd = serviceListJsonLd(SERVICES, '/verwalten')

export default function Verwalten() {
  useSeo({
    title: 'Verwalten',
    description:
      'WEG-Verwaltung, Sondereigentumsverwaltung und Mietverwaltung: maßgeschneiderte Lösungen für Ihre Wohnungseigentümergemeinschaft, Ihre Eigentumswohnung oder Ihr Mehrfamilienhaus.',
    path: '/verwalten',
  })
  useJsonLd(servicesJsonLd, 'services')

  return (
    <>
      <PageHero
        eyebrow="Verwalten"
        title={`Professionelle ${hy('Immobilien', 'verwaltung')} für Ihren Erfolg`}
        lead="Entdecken Sie maßgeschneiderte Lösungen für Ihre WEG, Ihre Eigentumswohnung oder Ihr Mehrfamilienhaus."
        breadcrumb={[{ label: 'Verwalten' }]}
      >
        <div className="btn-row">
          <Button to="/kontakt" icon="arrowRight">
            Erstgespräch anfragen
          </Button>
          <Button to="/leistungen" variant="ghost-dark">
            Pakete &amp; Preise
          </Button>
        </div>
      </PageHero>

      {/* Quick jump between the three mandates */}
      <Section tone="paper" tight>
        <Reveal group className="grid grid--3">
          {SERVICES.map((service) => (
            <a
              className="card card--lift"
              href={`#${service.id}`}
              key={service.id}
              style={{ display: 'grid', gap: 'var(--s-3)' }}
            >
              <span className="icon-tile">
                <Icon name={service.art === 'keys' ? 'key' : service.art} />
              </span>
              <span
                style={{
                  fontFamily: 'var(--font-display)',
                  fontSize: 'var(--step-1)',
                  fontWeight: 700,
                  color: 'var(--text)',
                }}
              >
                {service.title}
              </span>
              <span style={{ fontSize: 'var(--step--1)', color: 'var(--text-body)' }}>
                {service.label}
              </span>
            </a>
          ))}
        </Reveal>
      </Section>

      <Section>
        {SERVICES.map((service) => (
          <div className="service-row" id={service.id} key={service.id}>
            <Reveal className="service-row__copy">
              <p className="eyebrow">{service.label}</p>
              <h2 className="service-row__title">{service.title}</h2>
              <p>{service.text}</p>
              <ul className="check-list">
                {service.points.map((point) => (
                  <li key={point}>
                    <Icon name="check" />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
              <div className="btn-row" style={{ marginTop: 'var(--s-5)' }}>
                <Button to="/kontakt" variant="outline" icon="arrowRight">
                  Angebot anfragen
                </Button>
              </div>
            </Reveal>

            <Reveal className="service-row__visual">
              <ServiceArt variant={service.art} />
            </Reveal>
          </div>
        ))}
      </Section>

      <Section tone="paper">
        <SectionHead
          eyebrow="Preisinformation"
          title="Drei Pakete, ein Anspruch"
          lead="Von der reinen Betriebskostenverwaltung bis zur strategischen Wertsteigerungsplanung. Welches Paket zu Ihrem Objekt passt, klären wir im Erstgespräch."
          center
        />
        <div
          className="btn-row"
          style={{ justifyContent: 'center', marginTop: 'calc(-1 * var(--s-4))' }}
        >
          <Button to="/leistungen" icon="arrowRight">
            Leistungen im Überblick
          </Button>
        </div>
      </Section>

      <Process />
      <CtaBand />
    </>
  )
}
